import React, { useEffect } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { fileLoadingSelector } from 'store/selectors';
import toast from 'react-hot-toast';
import { MdClose } from 'react-icons/md';
import Text from 'components/styled/Text';
import ModalContainer from 'components/styled/ModalContainer';
import { colors, FILE_TYPES } from '../../../../constants';
import FilePreview from '../FilePreview';
import Button from 'components/Button';

const FileViewerContainer = styled.div`
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  width: 524px;
  height: 572px;
  box-shadow: 0px 6px 12px 0px ${colors.navy}12;
`;

const TitleContainer = styled.div`
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid ${colors.grey95};
`;

const ButtonContainer = styled.div`
  padding: 24px;
  * {
    margin: 0;
  }
`;

const PreviewsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 24px 24px 24px;
  align-items: center;
  justify-content: flex-start;
  overflow: auto;
`;

const FileViewerModal = ({
  onClose,
  data,
  type,
  onAddMore,
  onFileClick,
  onDeleteFile,
}) => {
  const { t } = useTranslation();
  const isLoading = useSelector(fileLoadingSelector);

  const handleAddMoreButton = () => {
    onClose();
    onAddMore();
  };

  useEffect(() => {
    if (isLoading) {
      toast.loading(t('file.viewer.modal.toast.loading.message'));
    }
    if (!isLoading) {
      toast.dismiss();
    }
  }, [isLoading]); // eslint-disable-line

  return createPortal(
    <ModalContainer>
      <FileViewerContainer>
        <TitleContainer>
          <Text variant="title2">
            {type === FILE_TYPES.IMAGE_AND_VIDEO
              ? t('file.viewer.modal.photo.video.title')
              : t('file.viewer.modal.documents.title')}
          </Text>
          <MdClose onClick={onClose} fill={colors.navy} />
        </TitleContainer>
        <ButtonContainer>
          <Button size="medium" variant="primary" onClick={handleAddMoreButton}>
            {t('file.viewer.modal.add.more.button')}
          </Button>
        </ButtonContainer>
        <PreviewsList>
          {data.map(item => (
            <FilePreview
              key={item.id}
              item={item}
              onClick={onFileClick}
              onRemoveFile={onDeleteFile(item._id)}
            />
          ))}
        </PreviewsList>
      </FileViewerContainer>
    </ModalContainer>,
    document.body,
  );
};

FileViewerModal.propTypes = {
  type: PropTypes.oneOf(Object.values(FILE_TYPES)),
  onClose: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  onAddMore: PropTypes.func.isRequired,
  onFileClick: PropTypes.func.isRequired,
};

export default FileViewerModal;
